import React from 'react';
import EmpivoEvereeLogo from './EmpivoEvereeLogo';

const fmt = (d) => (d ? new Date(d).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '');

const Row = ({ done, title, detail }) => (
  <li className="flex gap-3">
    <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${done ? 'bg-green-100 text-green-700' : 'border-2 border-gray-300 text-gray-300'}`}>
      {done ? '✓' : ''}
    </span>
    <div>
      <p className={`text-sm font-semibold ${done ? 'text-gray-900' : 'text-gray-400'}`}>{title}</p>
      {detail && <p className="text-xs text-gray-500">{detail}</p>}
    </div>
  </li>
);

const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const IconCheckCircle = () => <Icon><circle cx="12" cy="12" r="9" /><path d="M8 12.5l2.7 2.7L16 9.8" /></Icon>;
const IconUsers = () => <Icon><circle cx="9" cy="8" r="3.2" /><path d="M3 19c0-3.2 2.7-5.2 6-5.2s6 2 6 5.2" /><path d="M16 5.2a3 3 0 010 5.6M18 14.2c1.8.6 3 2.2 3 4.8" /></Icon>;
const IconUserPlus = () => <Icon><circle cx="10" cy="8" r="3.2" /><path d="M4 19c0-3.2 2.7-5.2 6-5.2 1.2 0 2.3.3 3.2.8" /><path d="M18 14v6M15 17h6" /></Icon>;
const IconDoc = () => <Icon><rect x="5" y="3.5" width="14" height="17" rx="2.5" /><path d="M9 9h6M9 13h6" /></Icon>;
const IconCalendar = () => <Icon><rect x="4" y="5" width="16" height="15" rx="2.5" /><path d="M8 3v4M16 3v4M4 10h16M9 15l2 2 4-4" /></Icon>;
const IconShield = () => <Icon><path d="M12 3l7 3v5.5c0 4.3-2.9 8-7 9.5-4.1-1.5-7-5.2-7-9.5V6l7-3z" /><path d="M8.8 12.2l2.2 2.2 4.2-4.4" /></Icon>;

const Pill = ({ children, tone = 'gray' }) => (
  <span className={`inline-flex shrink-0 items-center rounded-full px-3 py-1 text-xs font-semibold ${tone === 'green' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
    {children}
  </span>
);

const NEXT_STEPS = [
  { icon: <IconUserPlus />, title: '1. Employee onboarding', text: 'Add employees and complete their required information.' },
  { icon: <IconDoc />, title: '2. Employment & payroll details', text: 'Complete employee records, pay details, and required documents.' },
  { icon: <IconCalendar />, title: '3. Payroll preparation', text: 'Review payroll settings and prepare for the first payroll run.' },
];

/** Shown once Empivo has switched the payroll service on */
const ActiveStatus = ({ onboarding: o }) => {
  const day = (d) => (d ? new Date(d).toLocaleDateString([], { dateStyle: 'long' }) : '');
  return (
    <div className="mx-auto max-w-3xl overflow-hidden rounded-lg bg-white shadow-xl ring-1 ring-black/5">
      <EmpivoEvereeLogo />
      <div className="space-y-5 px-5 py-8 md:px-10">
        {/* status */}
        <section className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6">
          <div className="flex items-start justify-between gap-3">
            <h1 className="text-xl font-bold text-gray-900 md:text-2xl">Payroll is active</h1>
            <Pill tone="green">Active</Pill>
          </div>
          <p className="mt-2 text-sm text-gray-600 md:text-base">Your company’s payroll account has been activated successfully by Everee.</p>
          <div className="mt-5 flex items-center gap-4 border-t border-gray-100 pt-5">
            <span className="text-green-600"><IconCheckCircle /></span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-gray-900">Payroll account activated</p>
              <p className="text-sm text-gray-500">{day(o.activatedAt)}</p>
            </div>
            <Pill>Completed</Pill>
          </div>
        </section>

        {/* next steps */}
        <section className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6">
          <h2 className="flex items-center gap-2 text-lg font-bold text-gray-900">
            <span className="text-gray-700"><IconUsers /></span> Next steps: HR team
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Your payroll account is ready. Your HR team can now continue with the following activities, subject to account permissions
            and Everee configuration.
          </p>
          <ul className="mt-4 divide-y divide-gray-100">
            {NEXT_STEPS.map((x) => (
              <li key={x.title} className="flex items-start gap-4 py-4 first:pt-2 last:pb-0">
                <span className="mt-0.5 text-gray-700">{x.icon}</span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900">{x.title}</p>
                  <p className="text-sm text-gray-500">{x.text}</p>
                </div>
                <Pill>HR</Pill>
              </li>
            ))}
          </ul>
        </section>

        {/* completed */}
        <section className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6">
          <div className="flex items-start gap-4">
            <span className="mt-0.5 text-gray-700"><IconShield /></span>
            <div>
              <p className="font-semibold text-gray-900">Payroll onboarding completed</p>
              <p className="text-sm text-gray-500">No further company onboarding action is required at this stage.</p>
            </div>
          </div>
          <p className="mt-4 text-sm text-gray-600">
            Employee management and ongoing payroll activities can be handled by authorized HR users through the appropriate Empivo or
            Everee screens.
          </p>
        </section>
      </div>
    </div>
  );
};

/** What the company sees after submitting (and whenever it returns to the Payroll page). */
const SubmissionStatus = ({ onboarding: o, payment }) => {
  const sent = ['Submitted to Everee', 'Active'].includes(o.status);
  const active = o.status === 'Active';
  const open = (o.infoRequests || []).filter((i) => !i.resolvedAt);

  if (active) return <ActiveStatus onboarding={o} />;

  return (
    <div className="mx-auto max-w-3xl overflow-hidden rounded-lg bg-white shadow-xl ring-1 ring-black/5">
      <EmpivoEvereeLogo />
      <div className="px-6 py-10 md:px-12">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-700">Payroll</p>
        <h1 className="mt-1 text-2xl font-bold text-gray-900">
          {active ? 'Payroll is active' : sent ? 'Your payroll setup is under review' : 'Your payroll setup was received'}
        </h1>
        {sent && !active ? (
          <div className="mt-2 space-y-3 text-gray-600">
            <p>
              Your payroll information and documents have been successfully submitted to Everee. Their team is reviewing your account
              to complete the verification process.
            </p>
            <p>
              <b className="text-gray-900">No further action is required at this time.</b> We’ll notify you once your payroll account
              is approved or if any additional information is needed.
            </p>
          </div>
        ) : (
          <p className="mt-2 text-gray-600">
            {active
              ? 'Everee has activated your payroll account.'
              : 'Our team is reviewing your submission and will send it to Everee shortly.'}
          </p>
        )}

        {o.status === 'Information Requested' && open.length > 0 && (
          <div className="mt-5 rounded border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-900">
            <b>We need a little more information:</b> {open[open.length - 1].message}
          </div>
        )}

        <ul className="mt-8 space-y-4 border-t border-gray-100 pt-6">
          <Row done title="Setup fee paid" detail={payment?.paidAt ? fmt(payment.paidAt) : undefined} />
          <Row done title="Forms and documents submitted" detail={fmt(o.createdAt)} />
          <Row done={sent} title="Sent to Everee" detail={o.submissionLog?.sentAt ? fmt(o.submissionLog.sentAt) : undefined} />
          <Row done={active} title="Payroll active" detail={o.activatedAt ? fmt(o.activatedAt) : undefined} />
        </ul>
      </div>
    </div>
  );
};

export default SubmissionStatus;